import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { SubmissionError } from 'redux-form';
import { formValueSelector } from 'redux-form';
import { bindActionCreators } from 'redux';
import { pick, get, omit, chain } from 'lodash';

import CommitForm, { FORM_NAME } from '../../components/commit-form';
import { reportError } from '../../../lib/error-reporter';
import MemberFieldsForm, {
  ALLOWED_MEMBER_FIELDS_PROPS,
} from '../../components/commit-form/member-fields-form';
import {
  fetchPurchase,
  submitOrgCommitForm,
  skipCommit,
  projectPurchase,
  setCommitFormVersion,
  showModalTopAlert,
  POST_CLAIM_COMMIT,
  COMMIT_NAMESPACE,
} from '../../actions';
import { formatContentWithEmoji } from '../../components/alert';
import {
  getMemberConsumerFieldSchemas,
  getOrganizerMembership,
  getCurrentPurchase,
  getCurrentProjectedPurchase,
} from '../../selectors/purchase-selector';
import { buildDesktopConsumerFieldValues, buildSpecialConsumerFieldInitialValues } from '../../lib/functions';
import { tokenizeCard } from '../../lib/card-functions';
import Membership from '../../lib/membership';
import Purchase from '../../lib/purchase';
import buildMemberFieldValues from '../../lib/desktop-member-fields-initial-values-builder';
import { formatMessage } from '../../lib/intl';
import { postComponentInfo } from '../../lib/iframe-messages';

const i18n = defineMessages({
  checkEmail: {
    id: 'organizerCommit.errors.checkEmail',
    defaultMessage: 'check your email',
  },
  checkPassword: {
    id: 'organizerCommit.errors.checkPassword',
    defaultMessage: 'check your password',
  },
  freePurchaseAlertTitle: {
    id: 'organizerCommit.freePurchaseAlert.title',
    defaultMessage: 'Use “Skip for now” if you’d like to make this purchase free for you.',
  },
});

const COMPONENT_ID = 'DesktopOrganizerCommitContainer';

class CommitFormContainer extends Component {
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSkip = this.onSkip.bind(this);
    this.finishSubmit = this.finishSubmit.bind(this);
    this.showSubmitionErrorWarnings = this.showSubmitionErrorWarnings.bind(this);
    this.onMemberFieldsContinue = this.onMemberFieldsContinue.bind(this);
    this.onEditMemberFields = this.onEditMemberFields.bind(this);
    this.disableSubmit = this.disableSubmit.bind(this);
    this.enableSubmit = this.enableSubmit.bind(this);
    this.formatMessage = formatMessage(this.props.intl);

    this.state = {
      submitDisabled: false,
      showMemberFields: props.memberConsumerFieldSchemas.length > 0 ||
        props.billingAddressConsumerFieldSchema,
      allowPayDepositOnCommit: !this.isPerSlot(),
    };
  }

  componentWillMount() {
    this.props.actions.setCommitFormVersion(POST_CLAIM_COMMIT)
    this.projectPurchase()
  }

  componentWillReceiveProps(nextProps) {
    const { purchase, match } = nextProps;
    const slug = get(match, 'params.id');

    if (purchase && !this.canCommit(purchase)) {
      window.location = `/purchases/${slug}`;
    }
  }

  componentDidMount() {
    const { purchase } = this.props;
    postComponentInfo('organizer-commit');
    if (purchase.allowOrganizerForFree) {
      this.showFreePurchaseAlert();
    }
  }

  canCommit = (purchase) => {
    const hasSubmittedPayment = !!chain(purchase)
      .get('payments')
      .find(p => !!p.submittedAt)
      .value();

    if (hasSubmittedPayment) return false;
    return true;
  }

  isPerSlot() {
    const { purchase } = this.props;
    const costType = get(purchase, 'product.costType');
    return Purchase.isPerSlot(costType);
  }

  disableSubmit() {
    this.setState({
      submitDisabled: true,
    });
  }

  enableSubmit() {
    this.setState({
      submitDisabled: false,
    });
  }

  onSkip(props) {
    const { authentication: { token } } = this.props;
    return this.props.actions.skipCommit(props, token)
      .then(() => this.redirectToDashboard());
  }

  onMemberFieldsContinue() {
    this.setState({
      showMemberFields: false,
      allowPayDepositOnCommit: false,
    });
  }

  onEditMemberFields() {
    this.setState({ showMemberFields: true });
  }

  onSubmit(props) {
    this.setState({ submitDisabled: true });

    if (props.skip) return this.onSkip(props);

    const {
      user = {},
      purchase,
      authentication,
      memberConsumerFieldSchemas,
      billingAddressConsumerFieldSchema
    } = this.props;

    const email = user.email || props.auth.email;

    const consumerFieldValues =
      buildDesktopConsumerFieldValues(props, { ...memberConsumerFieldSchemas, billingAddressConsumerFieldSchema });
    const data = { consumerFieldValues, ...omit(props, 'memberFields') };

    this.formProps = {
      ...data,
      user
    };

    if (props.card.paymentSourceId && props.card.paymentSourceId !== 'new') {
      return this.props.actions.submitOrgCommitForm(purchase, this.formProps, authentication.token)
        .then(() => this.redirectToDashboard())
        .catch((error) => {
          this.setState({ submitDisabled: false });
          const params = omit(this.formProps, 'auth');
          reportError({
            error,
            component: COMPONENT_ID,
            params,
          });

          if (error.is401) {
            throw new SubmissionError({
              organizerEmail: this.formatMessage(i18n.checkEmail),
              organizerPassword: this.formatMessage(i18n.checkPassword),
            });
          }
          const content = formatContentWithEmoji(error.message, '‼️');
          this.showSubmitionErrorWarnings(content);
        });
    }

    // When tokenize card finishes, the cardTokenized callback on CommitForm is called
    // See also card-fields.js
    tokenizeCard(props.card, email, purchase.paymentPlatform);
  }

  redirectToDashboard() {
    const { purchase } = this.props;
    const successReturnUrl = Purchase.getSuccessReturnUrlWithParams(purchase);

    if (successReturnUrl) {
      window.top.location = successReturnUrl;
      return;
    }

    window.top.location = `${window.location.origin}/purchases/${this.props.purchase.slug}`;
  }

  showSubmitionErrorWarnings(errorMessage) {
    let text;
    if (errorMessage) {
      text = <FormattedMessage
        id="organizerCommit.submitionError.withMessage"
        defaultMessage="Something went wrong, {errorMessage}."
        values={{ errorMessage }}
      />;
    } else {
      text = <FormattedMessage
        id="organizerCommit.submitionError.noMessage"
        defaultMessage="Something went wrong."
      />;
    }
    const content = formatContentWithEmoji(text, '‼️');
    this.props.actions.showModalTopAlert(content, 'warning');
    this.setState({ submitDisabled: false });
  }

  showFreePurchaseAlert() {
    const text = (
      <span>
        <span className="mr-4">
          <b>{this.formatMessage(i18n.freePurchaseAlertTitle)}</b>
        </span>
      </span>
    );
    const content = formatContentWithEmoji(text, '☝️');
    this.props.actions.showModalTopAlert(content, 'success');
  }

  finishSubmit(cardToken) {
    const { purchase, authentication: { token } } = this.props;

    const data = {
      ...this.formProps,
      card: {
        ...this.formProps.card,
        platformId: purchase.paymentPlatform.id,
        paymentSource: cardToken,
      },
    };

    return this.props.actions.submitOrgCommitForm(purchase, data, token)
      .then(() => this.redirectToDashboard())
      .catch((error) => {
        const content = formatContentWithEmoji(error.message, '‼️');
        this.showSubmitionErrorWarnings(content);
        reportError({
          error,
          component: COMPONENT_ID,
          params: { ...omit(data, 'auth'), token },
        });
      });
  }

  projectPurchase() {
    const { purchase, orgMembership } = this.props;
    const params = pick(purchase, ['id', 'group']);
    const claimedSlots = get(orgMembership, 'claimedSlots', 1) || 1;

    params.memberships = [
      {
        id: orgMembership.id,
        claimedSlots: claimedSlots,
        allowedShareAmount: orgMembership.allowedShareAmount || params.group.minContribution,
      },
    ];

    this.props.actions.projectPurchase(params, COMMIT_NAMESPACE);
  }

  render() {
    const { purchase, purchase: { merchant } } = this.props;
    if (this.state.showMemberFields) {
      return (
        <MemberFieldsForm
          {...pick(this.props, ALLOWED_MEMBER_FIELDS_PROPS)}
          showShareInfoInMemberFieldsForm={this.props.showShareInfoInMemberFieldsForm}
          onSubmit={this.onMemberFieldsContinue}
          allowPayDeposit={!this.isPerSlot()}
          allowSkip
        />
      )
    }

    const { projectedMembership, membershipOptedIn } = this.props;

    return (
      <CommitForm
        currentMembership={projectedMembership}
        membershipOptedIn={membershipOptedIn}
        user={this.props.user}
        editing={this.props.editing}
        purchase={purchase}
        initialValues={this.props.initialValues}
        onSubmit={this.onSubmit}
        onSkip={this.onSkip}
        onEditMemberFields={this.onEditMemberFields}
        cardTokenized={this.finishSubmit}
        cardError={this.showSubmitionErrorWarnings}
        submitDisabled={this.state.submitDisabled}
        disableSubmit={this.disableSubmit}
        enableSubmit={this.enableSubmit}
        hasMemberFields={this.props.memberConsumerFieldSchemas.length > 0}
        allowPayDeposit={this.state.allowPayDepositOnCommit}
        payDeposit={this.props.payDeposit}
        depositPayment={this.props.depositPayment}
        claimedSlots={this.props.claimedSlots}
        showOptInMarketing={merchant.requestOptInMarketing}
        allowSkip
      />
    );
  }
}

const mapStateToProps = (state) => {
  const { users, session, projections } = state;
  const user = users.byId[users.current];
  const purchase = getCurrentPurchase(state);

  if (!purchase) return {};

  const orgMembership = getOrganizerMembership(state);

  if (!orgMembership) return {};

  const projectedMembership = get(projections, `${COMMIT_NAMESPACE}.memberships.${orgMembership.id}`);

  const paymentSourceId = user.defaultPaymentSource ||
    user.paymentSources ? get(user.paymentSources, '[0]id', 'new') : 'new';

  const { group: { minContribution } } = purchase;
  const defaultContribution = Membership.defaultContributionAmount({
    membership: orgMembership,
    minContribution,
    currencyCode: purchase.currencyCode,
  });
  const selector = formValueSelector(FORM_NAME);
  const payDeposit = selector(state, 'reserveDeposit');

  let memberConsumerFieldSchemas = getMemberConsumerFieldSchemas(state);

  const billingAddressConsumerFieldSchema = get(getMemberConsumerFieldSchemas(state)
    .filter((field) => field.name === 'billing_address'), '[0]', null);

  const memberFieldValues = buildMemberFieldValues({
    schemas: memberConsumerFieldSchemas,
    persistedValues: get(orgMembership, 'consumerFieldValues', []),
    claimedSlots: orgMembership.claimedSlots,
  })
    .filter((field) => field.name !== 'billing_address');

  let billingAddressMemberFieldValue = null;
  let billingInitialValue = {};

  if (billingAddressConsumerFieldSchema && billingAddressConsumerFieldSchema.options.length) {
    billingAddressMemberFieldValue = JSON.stringify(get(billingAddressConsumerFieldSchema, 'options[0].value', null));
    billingInitialValue = {
      [billingAddressConsumerFieldSchema.name]: billingAddressMemberFieldValue,
    }
  }

  const showShareInfoInMemberFieldsForm = getMemberConsumerFieldSchemas(state).length > 0;

  memberConsumerFieldSchemas = getMemberConsumerFieldSchemas(state)
    .filter((field) => field.name !== 'billing_address');

  const specialConsumerFieldValues = buildSpecialConsumerFieldInitialValues(memberConsumerFieldSchemas);

  if (memberFieldValues.length) {
    memberFieldValues[0] = { ...memberFieldValues[0], ...specialConsumerFieldValues };
  } else {
    memberFieldValues.push(specialConsumerFieldValues);
  }

  const depositPayment = Purchase.isMultiPayment(purchase.payments) ?
    get(getCurrentProjectedPurchase(state, COMMIT_NAMESPACE), 'payments[0]', null) : null;

  return {
    authentication: session.authentication,
    user,
    editing: false,
    purchase,
    orgMembership,
    projectedMembership,
    membershipOptedIn: get(orgMembership, 'optInMarketing', false),
    organizerSignup: false,
    capturedMemberFieldValues: state.purchases.captured,
    memberConsumerFieldSchemas,
    showShareInfoInMemberFieldsForm,
    billingAddressConsumerFieldSchema,
    payDeposit,
    depositPayment,
    claimedSlots: selector(state, 'claimedSlots'),
    initialValues: {
      allowedShareAmount: defaultContribution,
      claimedSlots: 1,
      skip: false,
      optInMarketing: false,
      memberFields: {
        slots: memberFieldValues.length > 0 ? memberFieldValues : [{}],
      },
      ...billingInitialValue,
      card: {
        paymentSourceId,
      },
      auth: {
        email: purchase ? purchase.group.organizerEmail : '',
      },
    },
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators({
      fetchPurchase,
      submitOrgCommitForm,
      skipCommit,
      projectPurchase,
      setCommitFormVersion,
      showModalTopAlert,
    }, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(CommitFormContainer));



// WEBPACK FOOTER //
// ./src/desktop/containers/organizer-commit/index.container.js